import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, FormControl } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MainLayout from "layouts/Main";
import { NORD_POOL_TABS } from "helpers/tabs";
import dayjs from "dayjs";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";
import GavelIcon from "@mui/icons-material/Gavel";
import { ProtectedPaths } from "routes";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useAppDispatch, useAppSelector } from "hooks";
import AuctionSupplyPlansTable from "./AuctionSupplyPlansTable";
import { selectNordPoolSupplyPlans } from "store/selectors";
import { fetchNordPoolSupplyPlans } from "store/appSlice";
import { SupplyPlan } from "api/models/NordPool";
import LoadingButton from "@mui/lab/LoadingButton";
import { generateNordPoolSupplyPlans } from "api/resources/nordPool";
import { auctionIds, hasNPSupplyPlanDeadlinePassed } from "helpers/auctions";

interface Row {
  date: string;
  supplyPlans: SupplyPlan[];
}

const NordPoolSupplyPlans = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const [generatingSupplyPlans, setGeneratingSupplyPlans] = useState<any[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [formattedDates, setFormattedDates] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(null);
  const [snackbar, setSnackbar] = useState<{
    type: string;
    message: string;
  } | null>(null);
  const dispatch = useAppDispatch();
  const nordPoolSupplyPlans = useAppSelector(selectNordPoolSupplyPlans);

  useEffect(() => {
    const fromDateParam = searchParams.get("from");
    const toDateParam = searchParams.get("to");

    if (fromDateParam) {
      setFromDate(dayjs(fromDateParam));
    } else {
      setFromDate(dayjs().subtract(3, "day"));
    }

    if (toDateParam) {
      setToDate(dayjs(toDateParam));
    } else {
      setToDate(dayjs().add(1, "day"));
    }
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(
        fetchNordPoolSupplyPlans(
          dayjs(fromDate).format("YYYY-MM-DD"),
          dayjs(toDate).format("YYYY-MM-DD"),
        ),
      );
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (fromDate && toDate) {
      navigate(
        `/${ProtectedPaths.NordPoolSupplyPlans}?from=${dayjs(fromDate).format("YYYY-MM-DD")}&to=${dayjs(toDate).format("YYYY-MM-DD")}`,
        {
          replace: true,
        },
      );
    }
  }, [fromDate, toDate, dispatch]);

  const auctions = [
    "SIDC_IntradayAuction3",
    "SIDC_IntradayAuction2",
    "SIDC_IntradayAuction1",
    "DayAhead",
  ];

  useEffect(() => {
    if (nordPoolSupplyPlans) {
      const supplyPlansBeingGenerated = nordPoolSupplyPlans.filter(
        (sp: SupplyPlan) =>
          sp.status === "PENDING" || sp.status === "IN_PROGRESS",
      );

      setGeneratingSupplyPlans(supplyPlansBeingGenerated);
      setIsPolling(supplyPlansBeingGenerated.length > 0);
    }
  }, [nordPoolSupplyPlans]);

  useEffect(() => {
    const start = dayjs(fromDate).startOf("day");
    const end = dayjs(toDate).startOf("day");
    const dates = [];

    const totalDays = end.diff(start, "day");

    for (let i = 0; i <= totalDays; i++) {
      dates.push(start.add(i, "day").format("YYYY-MM-DD"));
    }

    setFormattedDates(dates);
  }, [fromDate, toDate]);

  const rows: Row[] = useMemo(() => {
    if (!formattedDates || !nordPoolSupplyPlans) return [];

    const tempRows: Row[] = [];

    formattedDates.forEach((date) => {
      const matchingGroups = nordPoolSupplyPlans.filter(
        (supplyPlan) =>
          supplyPlan.targetDate === date &&
          !generatingSupplyPlans.some(
            (gsp) => gsp.supplyPlanId === supplyPlan.id,
          ),
      );

      tempRows.push({
        date,
        supplyPlans: [...matchingGroups].sort((a, b) =>
          dayjs(b.createdAt).diff(dayjs(a.createdAt)),
        ),
      });
    });

    nordPoolSupplyPlans.forEach((supplyPlan) => {
      if (!tempRows.find((row) => row.date === supplyPlan.targetDate)) {
        tempRows.push({
          date: supplyPlan.targetDate,
          supplyPlans: [supplyPlan].sort((a, b) =>
            dayjs(b.createdAt).diff(dayjs(a.createdAt)),
          ),
        });
      }
    });

    return tempRows
      .sort((a, b) => dayjs(b.date).diff(dayjs(a.date)))
      .filter(
        (tempRow) =>
          dayjs(tempRow.date) >= fromDate && dayjs(tempRow.date) <= toDate,
      );
  }, [formattedDates, nordPoolSupplyPlans, generatingSupplyPlans]);

  const handleGenerateSupplyPlans = (date: string, auction: string) => {
    setLoading(date + "-" + auction);
    const auctionTypeId = auctionIds[auction as keyof typeof auctionIds];

    generateNordPoolSupplyPlans(date, auctionTypeId)
      .then((result) => {
        const portfolioDetails =
          result?.portfolioDetails ?? result?.data?.portfolioDetails ?? [];
        setGeneratingSupplyPlans(portfolioDetails);
        dispatch(
          fetchNordPoolSupplyPlans(
            dayjs(fromDate).format("YYYY-MM-DD"),
            dayjs(toDate).format("YYYY-MM-DD"),
          ),
        );
        setIsPolling(true);
        setLoading(null);
      })
      .catch((error) => {
        console.error(error);
        setLoading(null);
        setSnackbar({
          type: "error",
          message:
            error.response?.data?.message ||
            error.message ||
            "Failed to generate supply plans",
        });
      });
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isPolling) {
      const pollSupplyPlans = async () => {
        const shouldPoll = nordPoolSupplyPlans?.some(
          (sp) => sp.status === "PENDING" || sp.status === "IN_PROGRESS",
        );

        dispatch(
          fetchNordPoolSupplyPlans(
            dayjs(fromDate).format("YYYY-MM-DD"),
            dayjs(toDate).format("YYYY-MM-DD"),
          ),
        );

        if (!shouldPoll) {
          clearInterval(intervalId);
          setIsPolling(false);
        }
      };

      pollSupplyPlans();

      intervalId = setInterval(pollSupplyPlans, 5000);

      return () => clearInterval(intervalId);
    }
  }, [isPolling]);

  return (
    <MainLayout
      tabs={NORD_POOL_TABS}
      snackbar={{
        open: !!snackbar,
        handleClose: () => setSnackbar(null),
        type: snackbar?.type,
        message: snackbar?.message || "",
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Supply plans</Typography>
      </Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        <FormControl
          sx={{
            minWidth: 280,
            maxWidth: 280,
            background: "white",
            mr: 1,
          }}
          size="small"
        >
          <DatePicker
            slotProps={{ textField: { size: "small" } }}
            name="fromDate"
            value={fromDate}
            onChange={(value) => {
              setFromDate(dayjs(value));
            }}
            label="From date"
          />
        </FormControl>
        <FormControl
          sx={{
            minWidth: 280,
            maxWidth: 280,
            background: "white",
          }}
          size="small"
        >
          <DatePicker
            slotProps={{ textField: { size: "small" } }}
            name="toDate"
            value={toDate}
            onChange={(value) => {
              setToDate(dayjs(value));
            }}
            label="To date"
          />
        </FormControl>
      </Box>
      <Box sx={{ mt: 1 }}>
        {rows &&
          rows.map((row) => (
            <Accordion
              defaultExpanded={dayjs(row.date).isTomorrow()}
              sx={{ mb: "1rem", boxShadow: 2 }}
              key={"ForecastSampleAccordion-" + row.date}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "#eee",
                }}
              >
                <Typography
                  component="h6"
                  variant="h6"
                  fontSize="1.1rem"
                  fontWeight="400"
                >
                  {`${dayjs(row.date).format("DD.MM.YYYY")} - ${dayjs(
                    row.date,
                  ).format("dddd")}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {auctions.map((auction) => (
                    <Box sx={{ mt: 3 }} key={"Auction-" + auction}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1.5 }}
                      >
                        <Typography
                          component="p"
                          display="flex"
                          alignItems="center"
                          fontSize={16}
                          fontWeight={500}
                        >
                          <GavelIcon sx={{ width: 20, height: 20, mr: 1 }} />
                          {auction}
                        </Typography>
                        {!hasNPSupplyPlanDeadlinePassed(
                          auction,
                          dayjs(row.date),
                        ) && (
                          <LoadingButton
                            variant="outlined"
                            size="small"
                            loading={loading === row.date + "-" + auction}
                            loadingIndicator="Initializing..."
                            onClick={() =>
                              handleGenerateSupplyPlans(row.date, auction)
                            }
                            disabled={loading === row.date + "-" + auction}
                          >
                            Generate supply plans
                          </LoadingButton>
                        )}
                      </Box>
                      <AuctionSupplyPlansTable
                        loading={loading === row.date + "-" + auction}
                        supplyPlans={row.supplyPlans.filter(
                          (supplyPlan) =>
                            supplyPlan.nordpoolAuctionTypeName === auction,
                        )}
                        dispatch={() =>
                          dispatch(
                            fetchNordPoolSupplyPlans(
                              dayjs(fromDate).format("YYYY-MM-DD"),
                              dayjs(toDate).format("YYYY-MM-DD"),
                            ),
                          )
                        }
                        setSnackbar={setSnackbar}
                      />
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
      </Box>
    </MainLayout>
  );
};

export default NordPoolSupplyPlans;
